import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Clock, Calendar } from 'lucide-react';
import { format, parseISO, differenceInSeconds } from 'date-fns';

const CountdownBanner = ({ nextGame, showCountdown = true }) => {
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });

  useEffect(() => {
    if (!nextGame || !showCountdown) return;

    const calculateTimeLeft = () => {
      const now = new Date();
      const eventDate = parseISO(nextGame.date);
      const totalSeconds = Math.max(0, differenceInSeconds(eventDate, now));

      const days = Math.floor(totalSeconds / (24 * 60 * 60));
      const hours = Math.floor((totalSeconds % (24 * 60 * 60)) / (60 * 60));
      const minutes = Math.floor((totalSeconds % (60 * 60)) / 60);
      const seconds = totalSeconds % 60;

      setTimeLeft({ days, hours, minutes, seconds });
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);

    return () => clearInterval(timer);
  }, [nextGame, showCountdown]);

  if (!nextGame || !showCountdown) return null;

  const isLessThan24Hours = timeLeft.days === 0 && timeLeft.hours < 24;
  const isLessThan1Hour = timeLeft.days === 0 && timeLeft.hours === 0;

  return (
    <div className="card bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border-blue-200 dark:border-blue-800">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <Clock className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            <span className="text-sm font-medium text-blue-900 dark:text-blue-100">
              Next Game:
            </span>
          </div>
          <div className="flex items-center space-x-1">
            <Calendar className="w-4 h-4 text-blue-600 dark:text-blue-400" />
            <span className="text-sm text-blue-700 dark:text-blue-300">
              {format(parseISO(nextGame.date), 'MMM do, yyyy')}
            </span>
          </div>
        </div>
        
        <div className="flex items-center space-x-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-900 dark:text-blue-100">
              {timeLeft.days}
            </div>
            <div className="text-xs text-blue-600 dark:text-blue-400">Days</div>
          </div>
          <div className="text-blue-600 dark:text-blue-400">:</div>
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-900 dark:text-blue-100">
              {String(timeLeft.hours).padStart(2, '0')}
            </div>
            <div className="text-xs text-blue-600 dark:text-blue-400">Hours</div>
          </div>
          <div className="text-blue-600 dark:text-blue-400">:</div>
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-900 dark:text-blue-100">
              {String(timeLeft.minutes).padStart(2, '0')}
            </div>
            <div className="text-xs text-blue-600 dark:text-blue-400">Minutes</div>
          </div>
          <div className="text-blue-600 dark:text-blue-400">:</div>
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-900 dark:text-blue-100">
              {String(timeLeft.seconds).padStart(2, '0')}
            </div>
            <div className="text-xs text-blue-600 dark:text-blue-400">Seconds</div>
          </div>
          
          <Link
            to={`/events/${nextGame.id}`}
            className="btn btn-primary ml-4"
          >
            View Details
          </Link>
        </div>
      </div>
      
      {/* Urgency indicator */}
      {(isLessThan24Hours || isLessThan1Hour) && (
        <div className="mt-2 flex items-center space-x-2">
          <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
          <span className="text-sm font-medium text-red-700 dark:text-red-300">
            {isLessThan1Hour ? 'Less than 1 hour to go!' : 'Less than 24 hours to go!'}
          </span>
        </div>
      )}
    </div>
  );
};

export default CountdownBanner; 